import type { FieldDefinition, LayoutNode, SlideTypeDefinition } from "./index.js";

const canvas = {
  width: 1920,
  height: 1080,
  aspectRatio: "16:9",
  backgroundColor: "#07131f",
  safeArea: { top: 54, right: 72, bottom: 54, left: 72 },
};

const textField = (key: string, label: string, required = true): FieldDefinition => ({
  key,
  label,
  type: key === "description" ? "textarea" : "text",
  required,
  maxLength: key === "description" ? 500 : 140,
  localization: false,
  allowedClientOverrides: [],
});

const imageField = (key = "image", label = "Image"): FieldDefinition => ({
  key,
  label,
  type: key === "logo" ? "logo" : "image",
  required: true,
  accept: ["image/jpeg", "image/png", "image/webp"],
  maxFileSizeMb: 15,
  localization: false,
  allowedClientOverrides: [],
});

const imageNode = (
  binding = "image",
  styles: Record<string, string | number> = {},
): LayoutNode => ({
  type: binding === "logo" ? "logo" : "image",
  binding,
  styles: { width: "100%", height: "100%", objectFit: "cover", ...styles },
});

const textNode = (binding: string, variant: "title" | "description" = "title"): LayoutNode => ({
  type: "text",
  binding,
  variant,
});

const base = (
  key: string,
  name: string,
  fields: FieldDefinition[],
  layout: LayoutNode,
  description = `${name} data-driven signage template`,
): SlideTypeDefinition => ({
  key,
  name,
  description,
  version: 1,
  status: "published",
  purpose: "slide",
  canvas,
  fields,
  layout,
  defaultStyles: {},
  allowedStyleOverrides: ["color", "backgroundColor", "fontFamily", "fontSize", "textAlign"],
  animations: {
    entrance: { name: "fade-in", durationMs: 500, delayMs: 0 },
    exit: { name: "fade-out", durationMs: 300, delayMs: 0 },
  },
  availability: { global: true, clientIds: [] },
});

const hero = (alignment: "right" | "center"): LayoutNode => ({
  type: "container",
  styles: { position: "relative", width: "100%", height: "100%", overflow: "hidden" },
  children: [
    imageNode("backgroundImage"),
    {
      type: "overlay",
      styles: {
        position: "absolute",
        inset: 0,
        background:
          alignment === "right"
            ? "linear-gradient(90deg, transparent 20%, rgba(2,8,16,0.9) 100%)"
            : "rgba(2,8,16,0.55)",
      },
    },
    {
      type: "stack",
      styles: {
        position: "absolute",
        right: alignment === "right" ? "6%" : "14%",
        left: alignment === "right" ? "52%" : "14%",
        top: "50%",
        transform: "translateY(-50%)",
        textAlign: alignment,
        gap: 20,
      },
      children: [textNode("title"), textNode("description", "description")],
    },
  ],
});

const split = (imageLeft: boolean): LayoutNode => ({
  type: "grid",
  styles: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    width: "100%",
    height: "100%",
    backgroundColor: "#07131f",
  },
  children: imageLeft
    ? [
        imageNode(),
        {
          type: "stack",
          styles: { padding: 80, justifyContent: "center", gap: 18 },
          children: [textNode("title"), textNode("description", "description")],
        },
      ]
    : [
        {
          type: "stack",
          styles: { padding: 80, justifyContent: "center", gap: 18 },
          children: [textNode("title"), textNode("description", "description")],
        },
        imageNode(),
      ],
});

const commonTextFields = [
  textField("title", "Title"),
  textField("description", "Description", false),
];

export const builtInSlideTypes: SlideTypeDefinition[] = [
  base(
    "background-image-right-text",
    "Background image with right text",
    [imageField("backgroundImage", "Background image"), ...commonTextFields],
    hero("right"),
  ),
  base(
    "background-image-centered-text",
    "Background image with centered text",
    [imageField("backgroundImage", "Background image"), ...commonTextFields],
    hero("center"),
  ),
  base("image-only", "Image only", [imageField()], imageNode()),
  base(
    "logo-vertical-image-text",
    "Logo, vertical image and text",
    [imageField("logo", "Logo"), imageField(), ...commonTextFields],
    {
      type: "grid",
      styles: {
        display: "grid",
        gridTemplateColumns: "0.8fr 1.2fr",
        width: "100%",
        height: "100%",
      },
      children: [
        imageNode("image", { objectFit: "contain", padding: 70 }),
        {
          type: "stack",
          styles: { padding: 80, justifyContent: "center", gap: 24 },
          children: [
            imageNode("logo", { width: 180, height: 90, objectFit: "contain" }),
            textNode("title"),
            textNode("description", "description"),
          ],
        },
      ],
    },
  ),
  base("split-image-left", "Split image left", [imageField(), ...commonTextFields], split(true)),
  base("split-image-right", "Split image right", [imageField(), ...commonTextFields], split(false)),
  base(
    "solid-announcement",
    "Solid announcement",
    [
      ...commonTextFields,
      {
        key: "backgroundColor",
        label: "Background color",
        type: "color",
        required: true,
        defaultValue: "#0c7c86",
        localization: false,
        allowedClientOverrides: [],
      },
    ],
    {
      type: "stack",
      styles: {
        width: "100%",
        height: "100%",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: 120,
        backgroundColor: "#0c7c86",
      },
      children: [textNode("title"), textNode("description", "description")],
    },
  ),
  base(
    "quote-testimonial",
    "Quote or testimonial",
    [textField("quote", "Quote"), textField("attribution", "Attribution", false)],
    {
      type: "stack",
      styles: {
        width: "100%",
        height: "100%",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: 160,
        gap: 32,
      },
      children: [textNode("quote", "title"), textNode("attribution", "description")],
    },
  ),
  base(
    "qr-call-to-action",
    "QR code call to action",
    [
      textField("title", "Title"),
      {
        key: "url",
        label: "Destination URL",
        type: "url",
        required: true,
        localization: false,
        allowedClientOverrides: [],
      },
      textField("instructions", "Instructions", false),
    ],
    {
      type: "grid",
      styles: {
        display: "grid",
        gridTemplateColumns: "1.2fr 0.8fr",
        width: "100%",
        height: "100%",
      },
      children: [
        {
          type: "stack",
          styles: { padding: 100, justifyContent: "center", gap: 22 },
          children: [textNode("title"), textNode("instructions", "description")],
        },
        {
          type: "qrCode",
          binding: "url",
          styles: { alignSelf: "center", width: 420, height: 420 },
        },
      ],
    },
  ),
  base(
    "fullscreen-video",
    "Full-screen video",
    [
      {
        key: "video",
        label: "Video",
        type: "video",
        required: true,
        accept: ["video/mp4", "video/webm"],
        maxFileSizeMb: 100,
        localization: false,
        allowedClientOverrides: [],
      },
    ],
    {
      type: "video",
      binding: "video",
      styles: { width: "100%", height: "100%", objectFit: "cover" },
    },
  ),
  base(
    "date-time-logo-announcement",
    "Date, time, logo and announcement",
    [imageField("logo", "Logo"), ...commonTextFields],
    {
      type: "stack",
      styles: { width: "100%", height: "100%", padding: 90, justifyContent: "space-between" },
      children: [
        imageNode("logo", { width: 220, height: 100, objectFit: "contain" }),
        { type: "dateTime", styles: { fontSize: 92, fontWeight: 700 } },
        {
          type: "stack",
          styles: { gap: 16 },
          children: [textNode("title"), textNode("description", "description")],
        },
      ],
    },
  ),
  base(
    "multiple-image-grid",
    "Multiple image grid",
    [
      imageField("image1", "Image 1"),
      imageField("image2", "Image 2"),
      imageField("image3", "Image 3"),
      imageField("image4", "Image 4"),
    ],
    {
      type: "grid",
      styles: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        width: "100%",
        height: "100%",
        gap: 10,
      },
      children: [
        imageNode("image1"),
        imageNode("image2"),
        imageNode("image3"),
        imageNode("image4"),
      ],
    },
  ),
];
